import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React, { useEffect } from "react";
import { MdHighlightOff } from "react-icons/md";
import { MultiSelect } from "react-multi-select-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const GLOBAL_ID = '__dr_global_styles__';
function injectGlobalStyles() {
  if (typeof document === 'undefined' || document.getElementById(GLOBAL_ID)) return;
  const s = document.createElement('style');
  s.id = GLOBAL_ID;
  s.textContent = `
    /* ── DateRange global overrides ── */
    .react-datepicker-popper { z-index: 99999 !important; }
    .react-datepicker {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      border: 1.5px solid #e2e8f0 !important;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(15,23,42,0.14) !important;
      overflow: hidden;
    }
    .react-datepicker__header {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
      border-radius: 0 !important;
      padding-top: 10px !important;
    }
    .react-datepicker__current-month {
      font-size: 13px !important; font-weight: 700 !important; color: #0f172a !important;
    }
    .react-datepicker__day-name {
      font-size: 11px !important; font-weight: 700 !important; color: #94a3b8 !important;
    }
    .react-datepicker__day {
      font-size: 12.5px !important; font-weight: 500 !important; color: #374151 !important; border-radius: 7px !important;
    }
    .react-datepicker__day:hover { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__day--selected,
    .react-datepicker__day--in-range,
    .react-datepicker__day--range-start,
    .react-datepicker__day--range-end { background: #2563eb !important; color: #fff !important; }
    .react-datepicker__day--in-selecting-range { background: #bfdbfe !important; color: #1d4ed8 !important; }
    .react-datepicker__day--keyboard-selected { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
    .react-datepicker__close-icon::after {
      background-color: #94a3b8 !important; font-size: 13px !important;
      width: 16px !important; height: 16px !important;
    }
    .react-datepicker__close-icon:hover::after { background-color: #dc2626 !important; }

    /* ── Mobile calendar: constrain to viewport ── */
    @media (max-width: 600px) {
      .react-datepicker-popper {
        /* Prevent it going off right edge */
        max-width: calc(100vw - 24px) !important;
        left: 12px !important;
        right: auto !important;
        /* Override any inline transform-based positioning */
        transform: translate3d(0px, 42px, 0px) !important;
      }
      .react-datepicker {
        width: calc(100vw - 24px) !important;
        border-radius: 12px !important;
      }
      .react-datepicker__month-container {
        width: 100% !important;
        float: none !important;
      }
      /* Equally distribute day cells */
      .react-datepicker__week,
      .react-datepicker__day-names {
        display: flex !important;
        justify-content: space-between !important;
        padding: 0 6px !important;
      }
      .react-datepicker__day-name,
      .react-datepicker__day {
        flex: 1 !important;
        width: auto !important;
        margin: 1px 0 !important;
        font-size: 12px !important;
      }
      .react-datepicker__current-month { font-size: 12.5px !important; }
    }

    @media (max-width: 380px) {
      .react-datepicker-popper {
        max-width: calc(100vw - 16px) !important;
        left: 8px !important;
      }
      .react-datepicker { width: calc(100vw - 16px) !important; }
      .react-datepicker__day { font-size: 11px !important; }
      .react-datepicker__day-name { font-size: 10px !important; }
    }
  `;
  document.head.appendChild(s);
}

const FilterModal = ({
  clients,
  setPage,
  setFilter,
  filter,
  setLocalFilters,
  filterData,
  localFilters,
  setClearClick,
  isAllDataReady,
}: {
  clients?: any;
  setPage?: any;
  filter?: any;
  setFilter?: (e: any) => void;
  setLocalFilters?: (e: any) => void;
  localFilters?: any;
  filterData?: () => void;
  setClearClick?: () => void;
  isAllDataReady?: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isApplied, setIsApplied] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const hasActiveFilter = filter?.client?.length > 0 || filter?.dates;
  useEffect(() => {
      injectGlobalStyles();
      const check = () => setIsMobile(window.innerWidth <= 600);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);

  const originalFilterRef = React.useRef(filter);

  const handleClose = () => {
    if (isDirty && !isApplied) {
      setFilter(originalFilterRef.current);
    }
    setIsDirty(false);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  // const handleClose = () => {
  //   if (isDirty && !isApplied) {
  //     setFilter({ client: [], dates: null });
  //   }
  //   setIsDirty(false);
  //   setIsCloseModal(true);
  //   setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  // };

  const handleClear = () => {
    setFilter({ dates: null });
    setIsApplied(false);
    setClearClick?.();
  };

  const clientOptions = clients?.[0]?.map((m: any) => ({ label: m?.name, value: m?.client_id })) ?? [];
  const selectedClients = clientOptions.filter((f: any) => filter?.client?.includes(f?.value));

  const activeCount = [
    filter?.client?.length > 0,
    filter?.dates && Object.keys(filter.dates).length > 0,
  ].filter(Boolean).length;

  return (
    <>
      {/* Trigger */}
      <button
        className="fm-filter-btn"
        onClick={() => { originalFilterRef.current = filter; onOpen(); setIsApplied(false); }}
        disabled={!isAllDataReady}
        aria-label="Apply filter"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        {hasActiveFilter ? "Update Filter" : "Apply Filter"}
        {hasActiveFilter && <span className="af-trigger-badge">{activeCount}</span>}
      </button>

      {hasActiveFilter && (
        <button className="fm-clear-btn" onClick={handleClear} aria-label="Clear filters">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Clear Filter
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`fm-panel${isOpen && !isCloseModal ? " fm-panel--open" : " fm-panel--close"}`}>

          {/* Top glow */}
          <div className="fm-top-glow" />

          {/* Header */}
          <div className="fm-header">
            <div className="fm-header-brand">
              <div className="fm-header-logo">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </div>
              <div>
                <h2 className="fm-header-title">Filter Logs</h2>
                <p className="fm-header-sub">Narrow down by client and date range</p>
              </div>
            </div>
            <button className="fm-close-btn" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          {/* Body */}
          <div className="fm-body">

            {/* Client filter */}
            <div className="fm-section">
              <div className="fm-section-icon fm-section-icon--sky">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="fm-section-content">
                <label className="fm-label">Clients</label>
                <p className="fm-label-sub">Filter logs by one or more clients</p>
                <div className="fm-multiselect-wrap">
                  <div className={`comm-multiselect-wrap`}>
                    <MultiSelect
                      options={clientOptions}
                      value={selectedClients}
                      onChange={(e: any) => {
                        setIsDirty(true);
                        const vals = e.map((m: any) => m?.value);
                        setFilter?.((prev: any) => ({ ...prev, client: vals }));
                        setLocalFilters?.((prev: any) => ({ ...prev, client: vals }));
                      }}
                      labelledBy="Select clients"
                      isCreatable={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="fm-divider">
              <span className="fm-divider-label">Date</span>
              <div className="fm-divider-line" />
            </div>

            {/* Date filter */}
            <div className="fm-section">
              <div className="fm-section-icon fm-section-icon--sky">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="fm-section-content">
                <label className="fm-label">Date Range</label>
                <p className="fm-label-sub">Select a start and end date</p>
                <div className="dr-wrap">
                  <DatePicker 
                    onKeyDown={(e) => e.preventDefault()}
                    withPortal={isMobile}
                    portalId={isMobile ? "dr-portal" : undefined}
                    popperPlacement="bottom-start"
                    className="dr-input"
                    placeholderText="Select date range…"
                    startDate={filter?.start_date ? new Date(filter.start_date) : null}
                    endDate={filter?.end_date ? new Date(filter.end_date) : null}
                    selectsRange={true}
                    isClearable={true}
                    onChange={(dateRange) => {
                      setIsDirty(true);
                      const [a, b] = dateRange as any;
                      setFilter?.((prev: any) => ({
                        ...prev,
                        start_date: a,
                        end_date: b,
                        dates: a != null && b != null ? [a, b] : null,
                      }));
                    }}
                  />
                </div>
                {/* <div className="fm-datepicker-wrap">
                  <DatePicker 
                    onKeyDown={(e) => e.preventDefault()}
                    className="fm-datepicker"
                    placeholderText="Select date range…"
                    startDate={filter?.start_date ? new Date(filter.start_date) : null}
                    endDate={filter?.end_date ? new Date(filter.end_date) : null}
                    selectsRange={true}
                    isClearable={true}
                    onChange={(dateRange) => {
                      setIsDirty(true);
                      const [a, b] = dateRange as any;
                      setFilter?.((prev: any) => ({
                        ...prev,
                        start_date: a,
                        end_date: b,
                        dates: a != null && b != null ? [a, b] : null,
                      }));
                    }}
                  />
                </div> */}
              </div>
            </div>

 
            {hasActiveFilter && (
              <div className="afm-summary">
                <p className="afm-summary-label">Active filters:</p>
                <div className="afm-summary-chips">
                  {filter?.client?.length > 0 && (
                    <span className="afm-chip afm-chip--green">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      {`${filter.client.length} Client${filter.client.length !== 1 ? "s selected" : " selected"}`}
                    </span>
                  )}
                  {filter?.dates && (
                    <span className="afm-chip afm-chip--purple">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Date range set
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="fm-footer">
            <div className="fm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Click Apply to update results
            </div>
            
            <div className="fm-footer-btns">
                <button className="fm-btn-cancel" onClick={handleClose}>Cancel</button>
                <button
                    className="fm-btn-apply"
                    onClick={() => { setPage?.(1); filterData?.(); setIsApplied(true); setIsDirty(false); onClose(); }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Apply Filters
                </button>
            </div>
            
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .af-trigger-badge { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 5px; border-radius:9px; background:#fbbf24; color:#1e3a8a; font-size:10px; font-weight:800; border:1.5px solid rgba(255,255,255,.7); }

        /* Trigger */
        .fm-filter-btn {
          position: relative;
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #fff;
          padding: 9px 18px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#6d28d9,#7c3aed);
          cursor: pointer; box-shadow: 0 3px 12px rgba(124,58,237,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .fm-filter-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(124,58,237,.36); }
        .fm-filter-btn:disabled { opacity:.5; cursor:not-allowed; }

        .fm-clear-btn {
          display:inline-flex; align-items:center; gap:5px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:12.5px; font-weight:700; color:#e11d48;
          padding:9px 14px; border-radius:10px; border:1.5px solid #fecdd3; background:#fff1f2;
          cursor:pointer; transition:all .15s; white-space:nowrap;
        }
        .fm-clear-btn:hover { background:#ffe4e6; border-color:#fca5a5; }

        /* Panel */
        .fm-panel {
          font-family:'Plus Jakarta Sans',sans-serif;
          position:fixed; top:0; right:0; bottom:0;
          width:100%; max-width:520px;
          background:#fff; z-index:1004;
          display:flex; flex-direction:column;
          box-shadow:-12px 0 60px rgba(15,23,42,.18);
          transition:transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow:hidden;
        }
        .fm-panel--open  { transform:translateX(0); }
        .fm-panel--close { transform:translateX(100%); }

        .fm-top-glow {
          height:3px; flex-shrink:0;
          background:linear-gradient(90deg,#075985,#0284c7,#06b6d4,#0284c7,#075985);
          background-size:200% 100%; animation:fmGlow 3s linear infinite;
        }
        @keyframes fmGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .fm-header { padding:15px 26px; background:linear-gradient(135deg,#075985 0%,#0284c7 60%,#0ea5e9 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .fm-header-brand { display:flex; align-items:center; gap:13px; }
        .fm-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .fm-header-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .fm-header-sub   { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .fm-close-btn { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .fm-close-btn:hover { color:#fff; }

        /* Body */
        .fm-body { flex:1; overflow-y:auto; overflow-x:hidden; padding:24px 26px; display:flex; flex-direction:column; gap:18px; }
        .fm-body::-webkit-scrollbar { width:4px; }
        .fm-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        /* Section */
        .fm-section { display:flex; gap:14px; align-items:flex-start; }
        .fm-section-icon { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:2px; }
        .fm-section-icon--sky { background:linear-gradient(135deg,#f0f9ff,#e0f2fe); border:1.5px solid #bae6fd; color:#0284c7; }
        .fm-section-content { flex:1; display:flex; flex-direction:column; gap:6px; }
        .fm-label { font-size:13px; font-weight:700; color:#0f172a; }
        .fm-label-sub { font-size:12px; color:#94a3b8; font-weight:500; margin-bottom:4px; }

        /* Multiselect override */
        .fm-multiselect-wrap .rmsc { --rmsc-main:#0284c7 !important; --rmsc-border:#e2e8f0 !important; --rmsc-radius:11px !important; --rmsc-p:10px !important; font-family:'Plus Jakarta Sans',sans-serif !important; font-size:13.5px !important; }
        .fm-multiselect-wrap .rmsc .dropdown-heading { font-weight:600; }

        /* Divider */
        .fm-divider { display:flex; align-items:center; gap:10px; }
        .fm-divider-label { font-size:9.5px; font-weight:800; color:#b8c4d8; text-transform:uppercase; letter-spacing:1px; white-space:nowrap; }
        .fm-divider-line { flex:1; height:1px; background:linear-gradient(90deg,#e4e9f4,transparent); }
        .dr-wrap {
          position: relative; display: flex; align-items: center; width: 100%;
        }
        .dr-icon {
          position: absolute; left: 12px; color: #94a3b8;
          display: flex; align-items: center; pointer-events: none; z-index: 1;
        }
        .dr-input {
          width: 100%;
          height: 42px;
          padding: 0 36px 0 10px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 500; color: #0f172a;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          cursor: pointer; box-sizing: border-box; -webkit-appearance: none;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .dr-input::placeholder { color: #cbd5e1; }
        .dr-input:hover:not(:disabled) { border-color: #cbd5e1; background: #f1f5f9; }
        .dr-input:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .dr-input:disabled { opacity: 0.55; cursor: not-allowed; background: #f1f5f9; }

        /* Portal overlay on mobile */
        .react-datepicker__portal {
          background: rgba(15,23,42,0.45) !important;
          backdrop-filter: blur(3px) !important;
          z-index: 99999 !important;
        }
        .react-datepicker__portal .react-datepicker {
          border-radius: 18px !important;
          box-shadow: 0 24px 60px rgba(15,23,42,0.2) !important;
          overflow: hidden;
        }
        /* Make portal calendar scrollable on very short screens */
        .react-datepicker__portal .react-datepicker__month-container {
          max-height: 80vh; overflow-y: auto;
        }

        @media (max-width: 480px) {
          .dr-input { font-size: 12px; height: 40px; padding: 0 32px 0 10px;width: 266px !important; }
          .dr-icon svg { width: 13px; height: 13px; }
        }
        @media (max-width: 380px) {
          .dr-input { font-size: 11.5px; height: 38px;width: 266px !important; }
        }
        /* Date picker */
        .fm-datepicker-wrap .react-datepicker-wrapper { width:100%; }
        .fm-datepicker {
          width:100%; padding:11px 14px;
          background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:11px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; color:#0f172a;
          outline:none; transition:border-color .18s; box-sizing:border-box;
        }
        .fm-datepicker:focus { border-color:#0284c7; background:#fff; box-shadow:0 0 0 3px rgba(2,132,199,.1); }

        /* Active summary */
        .fm-active-summary { display:flex; gap:12px; align-items:flex-start; background:linear-gradient(135deg,#f0fdf4,#dcfce7); border:1.5px solid #bbf7d0; border-radius:12px; padding:14px 16px; }
        .fm-active-summary-icon { width:30px; height:30px; border-radius:8px; background:#fff; border:1.5px solid #bbf7d0; display:flex; align-items:center; justify-content:center; color:#16a34a; flex-shrink:0; }
        .fm-active-summary-title { font-size:13px; font-weight:700; color:#15803d; margin-bottom:2px; }
        .fm-active-summary-desc { font-size:12px; color:#16a34a; font-weight:500; }

        /* Summary */
        .afm-summary {
          margin-top: 4px; padding: 16px;
          background: linear-gradient(135deg,#faf5ff,#ede9fe);
          border: 1.5px solid #ddd6fe; border-radius: 12px;
        }
        .afm-summary-label { font-size: 11.5px; font-weight: 700; color: #6d28d9; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 10px; }
        .afm-summary-chips { display: flex; flex-wrap: wrap; gap: 7px; }
        .afm-chip {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; font-weight: 600;
          padding: 5px 10px; border-radius: 7px;
        }
        .afm-chip--green { background: #f0fdf4; border: 1.5px solid #bbf7d0; color: #15803d; }
        .afm-chip--purple { background: #ede9fe; border: 1.5px solid #c4b5fd; color: #6d28d9; }


        /* Footer */
        .fm-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-shrink:0; }
        .fm-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .fm-btn-cancel:hover { background:#f1f5f9; color:#374151; }
        .fm-footer-note { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .fm-footer-btns { display:flex; align-items:center; gap:8px; }
        .fm-btn-clear {
          display:inline-flex; align-items:center; gap:5px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#e11d48;
          padding:9px 14px; border-radius:10px; border:1.5px solid #fecdd3; background:#fff1f2;
          cursor:pointer; transition:all .15s;
        }
        .fm-btn-clear:hover { background:#ffe4e6; }
        .fm-btn-apply {
          display:inline-flex; align-items:center; gap:7px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff;
          padding:10px 20px; border-radius:10px; border:none;
          background:linear-gradient(135deg,#075985,#0284c7);
          cursor:pointer; box-shadow:0 4px 14px rgba(2,132,199,.28); transition:all .15s;
        }
        .fm-btn-apply:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(2,132,199,.36); }

        @media (max-width:640px) { .fm-panel { max-width:100%; } .fm-header, .fm-body, .fm-footer { padding-left:18px; padding-right:18px; } }
      `}</style>
    </>
  );
};

export default FilterModal;